import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  Shield,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface VerificationItem {
  id: number;
  item_name: string;
  status: 'verified' | 'pending' | 'rejected' | 'expired';
  verification_date?: string;
  expiry_date?: string;
  document_id?: number;
  notes?: string;
}

interface VerificationStatus {
  overall_status: 'verified' | 'pending' | 'rejected';
  verification_date: string;
  items: VerificationItem[];
  completion_percentage: number;
  provider_name?: string;
}

const ProviderVerificationStatusPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [verification, setVerification] = useState<VerificationStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchVerificationStatus();
    }
  }, [user?.id, authLoading]);

  const fetchVerificationStatus = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/verification`);
      if (response.success && response.data) {
        setVerification(response.data);
      }
    } catch (error) {
      console.error('Error fetching verification status:', error);
      toast({
        title: 'Error',
        description: 'Failed to load verification status',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'rejected':
        return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'expired':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Verification Status - VireWire Provider</title>
        <meta name="description" content="Check your verification status" />
      </Helmet>

      <div className="space-y-8">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : verification ? (
          <>
            {/* Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Verification Status</h1>
                <p className="text-gray-600 text-sm mt-1">{verification.provider_name || 'Provider Account'}</p>
              </div>
              <Button variant="outline" onClick={() => navigate('/provider/documents')}>
                Manage Documents
              </Button>
            </div>

            {/* Circular Progress */}
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-8 flex flex-col items-center justify-center text-center">
                <div className="relative w-32 h-32 mb-6 flex items-center justify-center">
                  <svg className="transform -rotate-90 w-32 h-32">
                    <circle cx="64" cy="64" r="56" stroke="#e5e7eb" strokeWidth="8" fill="none" />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="#10b981"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${(verification.completion_percentage / 100) * 2 * Math.PI * 56} ${2 * Math.PI * 56}`}
                      style={{ transition: 'stroke-dasharray 0.3s ease' }}
                    />
                  </svg>
                  <span className="absolute text-3xl font-bold text-gray-900">
                    {verification.completion_percentage}%
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-semibold">
                  {verification.items.length === 0
                    ? 'No documents uploaded yet'
                    : 'Documents Verified'}
                </p>
              </CardContent>
            </Card>

            {/* Verification Items List */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Verification Checklist</h3>
              {verification.items.length === 0 ? (
                <Card className="border-gray-200">
                  <CardContent className="p-8 text-center text-gray-600">
                    You haven't uploaded any documents yet. Upload your compliance documents to start the verification process.
                    <div className="mt-4">
                      <Button onClick={() => navigate('/provider/documents')} className="bg-green-600 hover:bg-green-700 text-white">
                        Upload Documents
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {verification.items.map((item) => (
                    <Card key={item.id} className={`border-2 ${getStatusColor(item.status)} hover:shadow-md transition-shadow`}>
                      <CardContent className="p-6">
                        <div className="flex items-start gap-3">
                          {getStatusIcon(item.status)}
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900">{item.item_name}</p>
                            <Badge
                              variant="secondary"
                              className={`mt-2 ${
                                item.status === 'verified'
                                  ? 'bg-green-100 text-green-800'
                                  : item.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Action Section */}
            {verification.overall_status !== 'verified' && (
              <Card className="bg-yellow-50 border-yellow-200 mt-8">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="font-semibold text-yellow-900 mb-2">Action Required</p>
                      <p className="text-sm text-yellow-800 mb-4">
                        {verification.items.length === 0
                          ? 'Upload your compliance documents to begin verification.'
                          : verification.overall_status === 'pending'
                          ? 'Your account verification is in progress. This typically takes 2-3 business days.'
                          : 'Your account verification needs attention. Please contact support for more details.'}
                      </p>
                      <Button
                        onClick={() => navigate('/provider/support')}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white"
                      >
                        Contact Support
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-600">No verification data found</p>
            </CardContent>
          </Card>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderVerificationStatusPage;
